import React, { useEffect, useState, useRef } from 'react';
import { 
  Plus, FileWarning, Search, Filter, Download, Trash2, Pencil, 
  Upload, X, FileText, User, Calendar, AlertTriangle, Eye
} from 'lucide-react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { formatDateTime, formatDate, cn } from '../lib/utils';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';

// Configuration des 4 niveaux d'avertissement
const LEVEL_CONFIG = {
  verbal: { 
    label: 'Avertissement verbal', 
    darkBg: 'bg-blue-500', 
    lightBg: 'bg-blue-500',
    darkText: 'text-white',
    lightText: 'text-white',
    severity: 1 
  },
  ecrit: { 
    label: 'Avertissement écrit', 
    darkBg: 'bg-amber-500', 
    lightBg: 'bg-amber-500',
    darkText: 'text-white',
    lightText: 'text-white',
    severity: 2 
  },
  rpe: { 
    label: 'RPE', 
    darkBg: 'bg-purple-500', 
    lightBg: 'bg-purple-500',
    darkText: 'text-white',
    lightText: 'text-white',
    severity: 3 
  },
  licenciement: { 
    label: 'Licenciement', 
    darkBg: 'bg-red-500', 
    lightBg: 'bg-red-500',
    darkText: 'text-white',
    lightText: 'text-white',
    severity: 4 
  }
};

export default function Warnings() {
  const [warnings, setWarnings] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterLevel, setFilterLevel] = useState('');
  const [filterEmployee, setFilterEmployee] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedWarning, setSelectedWarning] = useState(null);
  const [editingWarning, setEditingWarning] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const { hasRole, user } = useAuth();
  const { theme } = useTheme();

  const [formData, setFormData] = useState({
    employee_id: '',
    motif: '',
    niveau: 'verbal',
    description: '',
    date: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    if (hasRole(['admin', 'rh', 'responsable'])) {
      fetchWarnings();
      fetchEmployees();
    } else {
      setLoading(false);
    }
  }, []);

  const fetchWarnings = async () => {
    try {
      const response = await api.get('/warnings');
      setWarnings(response.data);
    } catch (error) {
      console.error('Error fetching warnings:', error);
      toast.error('Erreur lors du chargement des avertissements');
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await api.get('/employees');
      setEmployees(response.data.filter(e => e.actif !== false));
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const openCreateModal = () => {
    setEditingWarning(null);
    setFormData({
      employee_id: '',
      motif: '',
      niveau: 'verbal',
      description: '',
      date: new Date().toISOString().split('T')[0]
    });
    setShowModal(true);
  };

  const openEditModal = (warning) => {
    setEditingWarning(warning);
    setFormData({
      employee_id: warning.employee_id,
      motif: warning.motif,
      niveau: warning.niveau,
      description: warning.description,
      date: new Date(warning.date).toISOString().split('T')[0]
    });
    setShowModal(true);
  };

  const openDetailModal = async (warning) => {
    try {
      const response = await api.get(`/warnings/${warning.id}`);
      setSelectedWarning(response.data);
      setShowDetailModal(true);
    } catch (error) {
      toast.error('Erreur lors du chargement des détails');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.employee_id) {
      toast.error('Veuillez sélectionner un salarié');
      return;
    }

    try {
      const selectedEmployee = employees.find(emp => emp.id === formData.employee_id);
      
      if (editingWarning) {
        await api.put(`/warnings/${editingWarning.id}`, formData);
        toast.success('Avertissement modifié');
      } else {
        // Créer l'avertissement
        await api.post('/warnings', formData);
        
        // Envoyer l'email de notification
        try {
          await api.post('/warnings/send-notification', {
            employee_name: `${selectedEmployee?.prenom || ''} ${selectedEmployee?.nom || ''}`,
            employee_email: selectedEmployee?.email,
            niveau: formData.niveau,
            motif: formData.motif,
            description: formData.description,
            date: formData.date,
            created_by: `${user?.prenom || ''} ${user?.nom || ''}`
          });
          toast.success('Avertissement créé et notification envoyée');
        } catch (emailError) {
          console.error('Email notification error:', emailError);
          toast.success('Avertissement créé (notification email non envoyée)');
        }
      }
      setShowModal(false);
      fetchWarnings();
    } catch (error) {
      console.error('Error saving warning:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    }
  };

  const handleDelete = async (warning) => {
    if (!window.confirm(`Supprimer cet avertissement pour ${warning.employee_nom} ?`)) {
      return;
    }

    try {
      await api.delete(`/warnings/${warning.id}`);
      toast.success('Avertissement supprimé');
      fetchWarnings();
      if (showDetailModal) setShowDetailModal(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !selectedWarning) return;

    const formData = new FormData();
    formData.append('file', file);

    setUploading(true);
    try {
      await api.post(`/warnings/${selectedWarning.id}/documents`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Document ajouté');
      const response = await api.get(`/warnings/${selectedWarning.id}`);
      setSelectedWarning(response.data);
      fetchWarnings();
    } catch (error) {
      toast.error('Erreur lors de l\'upload');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDeleteDocument = async (docId) => {
    if (!selectedWarning) return;
    
    try {
      await api.delete(`/warnings/${selectedWarning.id}/documents/${docId}`);
      toast.success('Document supprimé');
      const response = await api.get(`/warnings/${selectedWarning.id}`);
      setSelectedWarning(response.data);
      fetchWarnings();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const downloadDocument = (doc) => {
    if (!doc.fichier_base64) {
      toast.error('Document non disponible');
      return;
    }
    
    const link = document.createElement('a');
    link.href = `data:application/octet-stream;base64,${doc.fichier_base64}`;
    link.download = doc.fichier_nom || doc.nom;
    link.click();
  };

  const exportToPDF = async (warning) => {
    try {
      const response = await api.get(`/warnings/export/pdf/${warning.id}`);
      const data = response.data;
      
      const printWindow = window.open('', '_blank');
      const html = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>Avertissement - ${data.warning.employee_nom}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 40px; max-width: 800px; margin: 0 auto; }
            .header { text-align: center; margin-bottom: 40px; border-bottom: 2px solid #333; padding-bottom: 20px; }
            .header h1 { margin: 0; font-size: 24px; }
            .header p { margin: 5px 0; color: #666; }
            .section { margin: 20px 0; }
            .section h2 { font-size: 16px; color: #333; border-bottom: 1px solid #ddd; padding-bottom: 5px; }
            .field { margin: 10px 0; }
            .field label { font-weight: bold; color: #555; }
            .field p { margin: 5px 0; }
            .level { display: inline-block; padding: 4px 12px; border-radius: 4px; font-weight: bold; color: white; }
            .level-verbal { background: #3b82f6; }
            .level-ecrit { background: #f59e0b; }
            .level-rpe { background: #a855f7; }
            .level-licenciement { background: #ef4444; }
            .footer { margin-top: 60px; padding-top: 20px; border-top: 1px solid #ddd; font-size: 12px; color: #666; }
            .signature { margin-top: 60px; display: flex; justify-content: space-between; }
            .signature-box { width: 200px; text-align: center; }
            .signature-line { border-top: 1px solid #333; margin-top: 50px; padding-top: 5px; }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>${data.company}</h1>
            <p>AVERTISSEMENT DISCIPLINAIRE</p>
          </div>
          
          <div class="section">
            <h2>Informations du Salarié</h2>
            <div class="field">
              <label>Nom complet:</label>
              <p>${data.employee?.prenom || ''} ${data.employee?.nom || ''}</p>
            </div>
            <div class="field">
              <label>Matricule:</label>
              <p>${data.employee?.matricule || 'N/A'}</p>
            </div>
            <div class="field">
              <label>Poste:</label>
              <p>${data.employee?.poste || 'N/A'}</p>
            </div>
          </div>
          
          <div class="section">
            <h2>Détails de l'Avertissement</h2>
            <div class="field">
              <label>Date:</label>
              <p>${new Date(data.warning.date).toLocaleDateString('fr-FR')}</p>
            </div>
            <div class="field">
              <label>Niveau:</label>
              <p><span class="level level-${data.warning.niveau}">${LEVEL_CONFIG[data.warning.niveau]?.label || data.warning.niveau}</span></p>
            </div>
            <div class="field">
              <label>Motif:</label>
              <p>${data.warning.motif}</p>
            </div>
            <div class="field">
              <label>Description:</label>
              <p>${data.warning.description}</p>
            </div>
          </div>
          
          <div class="signature">
            <div class="signature-box">
              <div class="signature-line">Signature de l'employeur</div>
            </div>
            <div class="signature-box">
              <div class="signature-line">Signature du salarié</div>
            </div>
          </div>
          
          <div class="footer">
            <p>Document généré le ${new Date().toLocaleDateString('fr-FR')} à ${new Date().toLocaleTimeString('fr-FR')}</p>
            <p>Par: ${data.generated_by}</p>
          </div>
        </body>
        </html>
      `;
      
      printWindow.document.write(html);
      printWindow.document.close();
      printWindow.print();
      
    } catch (error) {
      toast.error('Erreur lors de l\'export');
    }
  };

  // Filter warnings
  const filteredWarnings = warnings.filter(w => {
    const matchesSearch = 
      w.employee_nom?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      w.motif?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesLevel = !filterLevel || w.niveau === filterLevel;
    const matchesEmployee = !filterEmployee || w.employee_id === filterEmployee;
    return matchesSearch && matchesLevel && matchesEmployee;
  });

  // Group by employee for stats
  const statsByEmployee = warnings.reduce((acc, w) => {
    if (!acc[w.employee_id]) {
      acc[w.employee_id] = { nom: w.employee_nom, count: 0, levels: {} };
    }
    acc[w.employee_id].count++;
    acc[w.employee_id].levels[w.niveau] = (acc[w.employee_id].levels[w.niveau] || 0) + 1;
    return acc;
  }, {});

  // Helper pour obtenir les classes de badge selon le niveau
  const getLevelBadgeClasses = (niveau) => {
    const config = LEVEL_CONFIG[niveau];
    if (!config) return '';
    return cn(
      'px-3 py-1 rounded-md text-sm font-bold',
      theme === 'dark' ? config.darkBg : config.lightBg,
      theme === 'dark' ? config.darkText : config.lightText
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="relative">
          <div className={cn("w-16 h-16 border-4 rounded-full", theme === 'dark' ? 'border-zinc-800' : 'border-gray-200')}></div>
          <div className="absolute top-0 left-0 w-16 h-16 border-4 border-transparent border-t-red-500 rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  if (!hasRole(['admin', 'rh', 'responsable'])) {
    return (
      <div className={cn(
        "text-center py-12 rounded-xl border",
        theme === 'dark' ? 'bg-zinc-900/50 border-white/10 text-zinc-500' : 'bg-white border-gray-200 text-gray-500'
      )}>
        <p>Accès refusé</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 px-1 py-4 md:p-0 pb-24 md:pb-0" data-testid="warnings-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <FileWarning size={28} className="text-red-500" />
          <div>
            <h1 className={cn("text-2xl font-bold", theme === 'dark' ? 'text-white' : 'text-gray-900')}>
              Dossier Disciplinaire
            </h1>
            <p className={cn("text-sm", theme === 'dark' ? 'text-zinc-400' : 'text-gray-500')}>
              {warnings.length} avertissement(s) au total
            </p>
          </div>
        </div>
        <Button onClick={openCreateModal} variant="brand" data-testid="create-warning-button">
          <Plus size={18} className="mr-2" />
          <span className="hidden sm:inline">Nouvel Avertissement</span>
          <span className="sm:hidden">Nouveau</span>
        </Button>
      </div>

      {/* Stats Cards - 4 cartes en ligne sur desktop, 2x2 sur mobile */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
        {Object.entries(LEVEL_CONFIG).map(([key, config]) => {
          const count = warnings.filter(w => w.niveau === key).length;
          return (
            <div 
              key={key} 
              className={cn(
                "p-4 md:p-6 rounded-xl flex flex-col items-center justify-center text-center aspect-square md:aspect-auto md:py-8",
                config.darkBg,
                "text-white"
              )}
            >
              <div className="text-3xl md:text-4xl font-bold">{count}</div>
              <div className="text-xs md:text-sm mt-2 font-medium">{config.label}</div>
            </div>
          );
        })}
      </div>

      {/* Filters */}
      <div className={cn(
        "p-4 rounded-xl border",
        theme === 'dark' ? 'bg-zinc-900/50 border-white/10' : 'bg-white border-gray-200 shadow-sm'
      )}>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="relative">
            <Search size={18} className={cn(
              "absolute left-3 top-1/2 -translate-y-1/2",
              theme === 'dark' ? 'text-zinc-500' : 'text-gray-400'
            )} />
            <Input
              placeholder="Rechercher..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={cn(
                "pl-10",
                theme === 'dark' 
                  ? 'bg-zinc-800/50 border-white/10 text-white placeholder:text-zinc-500' 
                  : 'bg-gray-50 border-gray-200 text-gray-900 placeholder:text-gray-400'
              )}
              data-testid="search-input"
            />
          </div>
          
          <select
            value={filterLevel}
            onChange={(e) => setFilterLevel(e.target.value)}
            className={cn(
              "px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500",
              theme === 'dark'
                ? 'bg-zinc-800/50 border-white/10 text-white'
                : 'bg-gray-50 border-gray-200 text-gray-900'
            )}
            data-testid="filter-level"
          >
            <option value="">Tous les niveaux</option>
            {Object.entries(LEVEL_CONFIG).map(([key, config]) => (
              <option key={key} value={key}>{config.label}</option>
            ))}
          </select>
          
          <select
            value={filterEmployee}
            onChange={(e) => setFilterEmployee(e.target.value)}
            className={cn(
              "px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500",
              theme === 'dark'
                ? 'bg-zinc-800/50 border-white/10 text-white'
                : 'bg-gray-50 border-gray-200 text-gray-900'
            )}
            data-testid="filter-employee"
          >
            <option value="">Tous les salariés</option>
            {Object.entries(statsByEmployee).map(([id, data]) => (
              <option key={id} value={id}>{data.nom} ({data.count})</option>
            ))}
          </select>
          
          <div className={cn(
            "text-sm flex items-center",
            theme === 'dark' ? 'text-zinc-400' : 'text-gray-500'
          )}>
            {filteredWarnings.length} résultat(s)
          </div>
        </div>
      </div>

      {/* Warnings List */}
      <div className="space-y-4">
        {filteredWarnings.map((warning) => (
          <div
            key={warning.id}
            className={cn(
              "p-4 md:p-5 rounded-xl border transition-all",
              theme === 'dark' 
                ? 'bg-zinc-900/50 border-white/10 hover:border-white/20' 
                : 'bg-white border-gray-200 hover:border-gray-300 shadow-sm'
            )}
            data-testid={`warning-card-${warning.id}`}
          >
            <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
              <div className="flex-1">
                <div className="flex flex-wrap items-center gap-3 mb-2">
                  <h3 className={cn(
                    "font-semibold text-lg",
                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                  )}>
                    {warning.employee_nom}
                  </h3>
                  <span className={getLevelBadgeClasses(warning.niveau)}>
                    {LEVEL_CONFIG[warning.niveau]?.label || warning.niveau}
                  </span>
                  {warning.documents?.length > 0 && (
                    <span className={cn(
                      "flex items-center gap-1 px-2 py-1 rounded-md text-xs",
                      theme === 'dark' ? 'bg-zinc-700/50 text-zinc-300' : 'bg-gray-100 text-gray-600'
                    )}>
                      <FileText size={12} />
                      {warning.documents.length} doc(s)
                    </span>
                  )}
                </div>
                <p className={cn(
                  "text-sm font-medium mb-1",
                  theme === 'dark' ? 'text-zinc-300' : 'text-gray-700'
                )}>
                  <span className={theme === 'dark' ? 'text-zinc-500' : 'text-gray-400'}>Motif:</span> {warning.motif}
                </p>
                <p className={cn(
                  "text-sm mb-3 line-clamp-2",
                  theme === 'dark' ? 'text-zinc-400' : 'text-gray-500'
                )}>
                  {warning.description}
                </p>
                <div className={cn(
                  "flex flex-wrap items-center gap-4 text-xs",
                  theme === 'dark' ? 'text-zinc-500' : 'text-gray-400'
                )}>
                  <span className="flex items-center gap-1">
                    <Calendar size={12} />
                    {formatDate(warning.date)}
                  </span>
                  <span>Créé le {formatDateTime(warning.created_at)}</span>
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => openDetailModal(warning)}
                  className={cn(
                    theme === 'dark' 
                      ? 'bg-zinc-800/50 border-white/10 text-white hover:bg-white/10' 
                      : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100'
                  )}
                  data-testid={`view-warning-${warning.id}`}
                >
                  <Eye size={14} />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => exportToPDF(warning)}
                  title="Exporter en PDF"
                  className={cn(
                    theme === 'dark' 
                      ? 'bg-zinc-800/50 border-white/10 text-white hover:bg-white/10' 
                      : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100'
                  )}
                >
                  <Download size={14} />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => openEditModal(warning)}
                  className={cn(
                    theme === 'dark' 
                      ? 'bg-zinc-800/50 border-white/10 text-white hover:bg-white/10' 
                      : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100'
                  )}
                >
                  <Pencil size={14} />
                </Button>
                {hasRole(['admin', 'rh']) && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDelete(warning)}
                    className="text-red-500 hover:text-red-400 hover:bg-red-500/10 border-red-500/30"
                  >
                    <Trash2 size={14} />
                  </Button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredWarnings.length === 0 && (
        <div className={cn(
          "text-center py-12 rounded-xl border",
          theme === 'dark' ? 'bg-zinc-900/50 border-white/10' : 'bg-white border-gray-200 shadow-sm'
        )}>
          <FileWarning size={48} className={cn(
            "mx-auto mb-4",
            theme === 'dark' ? 'text-zinc-600' : 'text-gray-300'
          )} />
          <p className={theme === 'dark' ? 'text-zinc-500' : 'text-gray-500'}>
            Aucun avertissement trouvé
          </p>
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className={cn(
            "rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto border",
            theme === 'dark' ? 'bg-zinc-900 border-white/10' : 'bg-white border-gray-200'
          )}>
            <div className={cn(
              "flex items-center justify-between p-4 md:p-6 border-b",
              theme === 'dark' ? 'border-white/10' : 'border-gray-200'
            )}>
              <h2 className={cn(
                "text-lg md:text-xl font-semibold",
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              )}>
                {editingWarning ? 'Modifier l\'avertissement' : 'Nouvel Avertissement'}
              </h2>
              <button 
                onClick={() => setShowModal(false)} 
                className={cn(
                  "p-2 rounded-lg",
                  theme === 'dark' ? 'hover:bg-white/10 text-zinc-400' : 'hover:bg-gray-100 text-gray-500'
                )}
              >
                <X size={20} />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="p-4 md:p-6 space-y-4">
              <div>
                <Label htmlFor="employee" className={theme === 'dark' ? 'text-zinc-300' : 'text-gray-700'}>
                  Salarié *
                </Label>
                <select
                  id="employee"
                  value={formData.employee_id}
                  onChange={(e) => setFormData(prev => ({ ...prev, employee_id: e.target.value }))}
                  className={cn(
                    "w-full mt-1 px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500",
                    theme === 'dark'
                      ? 'bg-zinc-800/50 border-white/10 text-white'
                      : 'bg-gray-50 border-gray-200 text-gray-900'
                  )}
                  required
                  disabled={!!editingWarning}
                  data-testid="select-employee"
                >
                  <option value="">Sélectionner un salarié</option>
                  {employees.map(emp => (
                    <option key={emp.id} value={emp.id}>
                      {emp.prenom} {emp.nom} ({emp.matricule})
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="flex flex-col items-center">
                <Label htmlFor="date" className={cn('text-center', theme === 'dark' ? 'text-zinc-300' : 'text-gray-700')}>
                  Date *
                </Label>
                <Input
                  id="date"
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
                  className={cn(
                    "mt-1",
                    theme === 'dark' 
                      ? 'bg-zinc-800/50 border-white/10 text-white [&::-webkit-calendar-picker-indicator]:invert' 
                      : 'bg-gray-50 border-gray-200 text-gray-900'
                  )}
                  required
                  data-testid="input-date"
                />
              </div>
              
              {/* Sélection du niveau - 3 boutons centrés */}
              <div>
                <Label className={theme === 'dark' ? 'text-zinc-300' : 'text-gray-700'}>
                  Niveau *
                </Label>
                <div className="flex flex-col items-center gap-3 mt-3">
                  {Object.entries(LEVEL_CONFIG).map(([key, config]) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, niveau: key }))}
                      className={cn(
                        "w-full max-w-xs py-3 px-4 rounded-lg font-bold text-white transition-all",
                        config.darkBg,
                        formData.niveau === key 
                          ? 'ring-2 ring-offset-2 ring-offset-zinc-900 ring-white scale-105' 
                          : 'opacity-70 hover:opacity-100'
                      )}
                      data-testid={`level-${key}`}
                    >
                      {config.label}
                    </button>
                  ))}
                </div>
              </div>
              
              <div>
                <Label htmlFor="motif" className={theme === 'dark' ? 'text-zinc-300' : 'text-gray-700'}>
                  Motif *
                </Label>
                <Input
                  id="motif"
                  value={formData.motif}
                  onChange={(e) => setFormData(prev => ({ ...prev, motif: e.target.value }))}
                  placeholder="Ex: Retard répété, Absence injustifiée..."
                  className={cn(
                    "mt-1",
                    theme === 'dark' 
                      ? 'bg-zinc-800/50 border-white/10 text-white placeholder:text-zinc-500' 
                      : 'bg-gray-50 border-gray-200 text-gray-900 placeholder:text-gray-400'
                  )}
                  required
                  data-testid="input-motif"
                />
              </div>
              
              <div>
                <Label htmlFor="description" className={theme === 'dark' ? 'text-zinc-300' : 'text-gray-700'}>
                  Description détaillée *
                </Label>
                <textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Décrivez les faits de manière précise et factuelle..."
                  className={cn(
                    "w-full mt-1 px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500 min-h-[120px]",
                    theme === 'dark' 
                      ? 'bg-zinc-800/50 border-white/10 text-white placeholder:text-zinc-500' 
                      : 'bg-gray-50 border-gray-200 text-gray-900 placeholder:text-gray-400'
                  )}
                  required
                  data-testid="input-description"
                />
              </div>
              
              <div className={cn(
                "flex justify-center gap-3 pt-4 border-t",
                theme === 'dark' ? 'border-white/10' : 'border-gray-200'
              )}>
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setShowModal(false)}
                  className={cn(
                    theme === 'dark' 
                      ? 'bg-zinc-800/50 border-white/10 text-white hover:bg-white/10' 
                      : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100'
                  )}
                >
                  Annuler
                </Button>
                <Button type="submit" variant="brand" data-testid="submit-warning">
                  {editingWarning ? 'Enregistrer' : 'Créer l\'avertissement'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {showDetailModal && selectedWarning && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className={cn(
            "rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto border",
            theme === 'dark' ? 'bg-zinc-900 border-white/10' : 'bg-white border-gray-200'
          )}>
            <div className={cn(
              "flex items-center justify-between p-4 md:p-6 border-b",
              theme === 'dark' ? 'border-white/10' : 'border-gray-200'
            )}>
              <h2 className={cn(
                "text-lg md:text-xl font-semibold",
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              )}>
                Détails de l'avertissement
              </h2>
              <button 
                onClick={() => setShowDetailModal(false)} 
                className={cn(
                  "p-2 rounded-lg",
                  theme === 'dark' ? 'hover:bg-white/10 text-zinc-400' : 'hover:bg-gray-100 text-gray-500'
                )}
              >
                <X size={20} />
              </button>
            </div>
            
            <div className="p-4 md:p-6 space-y-6">
              {/* Employee Info */}
              <div className={cn(
                "flex items-start gap-4 p-4 rounded-lg",
                theme === 'dark' ? 'bg-zinc-800/50' : 'bg-gray-50'
              )}>
                <div className={cn(
                  "p-3 rounded-lg border",
                  theme === 'dark' ? 'bg-zinc-900 border-white/10' : 'bg-white border-gray-200'
                )}>
                  <User size={24} className={theme === 'dark' ? 'text-zinc-400' : 'text-gray-500'} />
                </div>
                <div>
                  <h3 className={cn(
                    "font-semibold text-lg",
                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                  )}>
                    {selectedWarning.employee_nom}
                  </h3>
                  <p className={cn(
                    "text-sm",
                    theme === 'dark' ? 'text-zinc-500' : 'text-gray-500'
                  )}>
                    ID: {selectedWarning.employee_id}
                  </p>
                </div>
              </div>
              
              {/* Warning Details */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className={theme === 'dark' ? 'text-zinc-500' : 'text-gray-500'}>Date</Label>
                  <p className={cn("font-medium", theme === 'dark' ? 'text-white' : 'text-gray-900')}>
                    {formatDate(selectedWarning.date)}
                  </p>
                </div>
                <div>
                  <Label className={theme === 'dark' ? 'text-zinc-500' : 'text-gray-500'}>Niveau</Label>
                  <p>
                    <span className={getLevelBadgeClasses(selectedWarning.niveau)}>
                      {LEVEL_CONFIG[selectedWarning.niveau]?.label || selectedWarning.niveau}
                    </span>
                  </p>
                </div>
              </div>
              
              <div>
                <Label className={theme === 'dark' ? 'text-zinc-500' : 'text-gray-500'}>Motif</Label>
                <p className={cn("font-medium", theme === 'dark' ? 'text-white' : 'text-gray-900')}>
                  {selectedWarning.motif}
                </p>
              </div>
              
              <div>
                <Label className={theme === 'dark' ? 'text-zinc-500' : 'text-gray-500'}>Description</Label>
                <p className={cn(
                  "whitespace-pre-wrap",
                  theme === 'dark' ? 'text-zinc-300' : 'text-gray-700'
                )}>
                  {selectedWarning.description}
                </p>
              </div>
              
              {/* Documents */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <Label className={theme === 'dark' ? 'text-zinc-500' : 'text-gray-500'}>
                    Documents joints
                  </Label>
                  <div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      onChange={handleFileUpload}
                      className="hidden"
                      accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading}
                      className={cn(
                        theme === 'dark' 
                          ? 'bg-zinc-800/50 border-white/10 text-white hover:bg-white/10' 
                          : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100'
                      )}
                    >
                      <Upload size={14} className="mr-2" />
                      {uploading ? 'Upload...' : 'Ajouter'}
                    </Button>
                  </div>
                </div>
                
                {selectedWarning.documents?.length > 0 ? (
                  <div className="space-y-2">
                    {selectedWarning.documents.map((doc) => (
                      <div 
                        key={doc.id} 
                        className={cn(
                          "flex items-center justify-between p-3 rounded-lg border",
                          theme === 'dark' ? 'bg-zinc-800/50 border-white/10' : 'bg-gray-50 border-gray-200'
                        )}
                      >
                        <div className="flex items-center gap-3">
                          <FileText size={18} className={theme === 'dark' ? 'text-zinc-500' : 'text-gray-400'} />
                          <span className={cn(
                            "text-sm font-medium",
                            theme === 'dark' ? 'text-white' : 'text-gray-900'
                          )}>
                            {doc.fichier_nom || doc.nom}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => downloadDocument(doc)}
                            className={theme === 'dark' ? 'text-zinc-400 hover:text-white' : 'text-gray-500 hover:text-gray-900'}
                          >
                            <Download size={14} />
                          </Button>
                          {hasRole(['admin', 'rh']) && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteDocument(doc.id)}
                              className="text-red-500 hover:text-red-400"
                            >
                              <Trash2 size={14} />
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className={cn(
                    "text-sm italic",
                    theme === 'dark' ? 'text-zinc-500' : 'text-gray-500'
                  )}>
                    Aucun document joint
                  </p>
                )}
              </div>
              
              {/* Actions */}
              <div className={cn(
                "flex flex-col sm:flex-row justify-between items-center gap-4 pt-4 border-t",
                theme === 'dark' ? 'border-white/10' : 'border-gray-200'
              )}>
                <div className={cn(
                  "text-xs",
                  theme === 'dark' ? 'text-zinc-500' : 'text-gray-500'
                )}>
                  Créé le {formatDateTime(selectedWarning.created_at)}
                </div>
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    onClick={() => exportToPDF(selectedWarning)}
                    className={cn(
                      theme === 'dark' 
                        ? 'bg-zinc-800/50 border-white/10 text-white hover:bg-white/10' 
                        : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100'
                    )}
                  >
                    <Download size={14} className="mr-2" />
                    PDF
                  </Button>
                  <Button 
                    variant="outline" 
                    onClick={() => {
                      setShowDetailModal(false);
                      openEditModal(selectedWarning);
                    }}
                    className={cn(
                      theme === 'dark' 
                        ? 'bg-zinc-800/50 border-white/10 text-white hover:bg-white/10' 
                        : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100'
                    )}
                  >
                    <Pencil size={14} className="mr-2" />
                    Modifier
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
